import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { knowledgebaseService } from '../Services/knowledgebaseService';
import toast from 'react-hot-toast';
import { PaginationListType } from '../Types/PaginationParams';
import { setListReloadType } from '../Store/features/environment/environmentSlice';
import useModal from '../components/Modal/useModal';
import { getBreakpoint } from '../utils/breakpoint';

const useKnowledgebases = () => {
    const navigate = useNavigate();
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const dispatch = useAppDispatch();
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const modal = useModal();
    const handleNavigate = useCallback(
        (id?: string) => {
            navigate(id ? `/admin/knowledgebases/${id}` : '/admin/knowledgebases/new');
        },
        [navigate],
    );

    const handleDelete = useCallback(
        async (id: string) => {
            try {
                await knowledgebaseService.deleteKnowledgebase(id);
                dispatch(setListReloadType(PaginationListType.Knowledgebases));

                toast.success(i18n.translate({ id: 'delete.success', sourceName: 'Knowledgebases' }));
            } catch (error) {
                console.error('Error deleting knowledgebase:', error);
                toast.error(i18n.translate({ id: 'delete.error', sourceName: 'Knowledgebases' }));
            }
        },
        [dispatch, i18n],
    );

    const handleOpenConfirmDialog = useCallback(
        (id: string) => {
            modal.deleteConfirm({
                title: i18n.translate({ id: 'delete.title', sourceName: 'Knowledgebases' }),
                content: i18n.translate({ id: 'delete.text', sourceName: 'Knowledgebases' }),
                onConfirm: () => handleDelete(id),
            });
        },
        [modal, handleDelete, i18n],
    );

    const KnowledgebasesActions = {
        handleDelete,
        handleOpenConfirmDialog,
    };

    return {
        KnowledgebasesActions,
        handleNavigate,
        i18n,
        windowWidth,
        getBreakpoint,
    };
};

export default useKnowledgebases;
